// src/utils/conditions.ts
// Mirrors backend/utils/assessmentScoring.js's evalCondition — same
// {field, op, value} condition shape used by ailment_templates scoring_rules
// — so a field's visible_if behaves identically to a scoring-rule condition.

export type ConditionOp = 'eq' | 'in' | 'includes' | 'gt' | 'gte' | 'lt' | 'lte' | 'nonEmpty';

export interface Condition {
  field: string;
  op: ConditionOp;
  value?: unknown;
}

export interface VisibleIfGroup {
  mode?: 'any' | 'all';
  conditions?: Condition[];
}

/** Any {key: value} bag of answers-so-far — checkbox-group arrays, radio
 * strings, free text, or a vitals sub-object, per PreConsultStage's template
 * answers shape. */
export type AnswerMap = Record<string, unknown>;

export function evalCondition(cond: Condition, answers: AnswerMap): boolean {
  const val = answers[cond.field];
  switch (cond.op) {
    case 'eq':       return val === cond.value;
    case 'in':       return Array.isArray(cond.value) && cond.value.includes(val);
    case 'includes': return Array.isArray(val) && val.includes(cond.value);
    case 'gt':       return Number(val) > Number(cond.value);
    case 'gte':      return Number(val) >= Number(cond.value);
    case 'lt':       return Number(val) < Number(cond.value);
    case 'lte':      return Number(val) <= Number(cond.value);
    case 'nonEmpty': return Array.isArray(val) ? val.length > 0 : !!val;
    default:         return false;
  }
}

/** A field with no visible_if is always visible. Otherwise visible_if is
 * { mode: 'any'|'all', conditions: [{field,op,value}, ...] }. */
export function isFieldVisible(field: { visible_if?: VisibleIfGroup }, answers: AnswerMap): boolean {
  const group = field.visible_if;
  if (!group || !Array.isArray(group.conditions) || !group.conditions.length) return true;
  return group.mode === 'all'
    ? group.conditions.every(c => evalCondition(c, answers))
    : group.conditions.some(c => evalCondition(c, answers));
}
